import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart3, MessageSquare, Send, FileText, Loader2, RefreshCw,
  CheckCircle, XCircle, ArrowUpRight, ArrowDownLeft, Users, Zap,
  Phone, Wifi, AlertTriangle, TrendingUp, Plus, Trash2, Edit3,
  Save, X, GitBranch, Bot, Clock, Tag, Search, Filter,
  Calendar, PlayCircle, PauseCircle, Settings, ChevronDown, ChevronRight,
  Eye, Copy
} from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import AdminLayout from '../../components/admin/AdminLayout';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Input from '../../components/ui/Input';
import WhatsAppInbox from './WhatsAppInbox';
import {
  getWaBalance, getWaStats, getWaTemplates, createWaTemplate, updateWaTemplate, deleteWaTemplate,
  getWaFlows, getWaFlow, createWaFlow, updateWaFlow, deleteWaFlow, saveWaFlowSteps, seedWaDefaultFlows,
  getWaAutoReplies, createWaAutoReply, updateWaAutoReply, deleteWaAutoReply,
  getWaContacts, updateWaContact, bulkTagWaContacts, getWaContactsByTag,
  getWaScheduled, createWaScheduled, cancelWaScheduled, processWaScheduled,
  sendWaBroadcast
} from '../../api/client';

type Tab = 'dashboard' | 'conversations' | 'templates' | 'flows' | 'auto_replies' | 'contacts' | 'broadcast' | 'scheduled';

export default function WhatsAppManager() {
  const [tab, setTab] = useState<Tab>('dashboard');
  const tabs: { key: Tab; label: string; icon: any }[] = [
    { key: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { key: 'conversations', label: 'Inbox', icon: MessageSquare },
    { key: 'templates', label: 'Templates', icon: FileText },
    { key: 'flows', label: 'Flows', icon: GitBranch },
    { key: 'auto_replies', label: 'Auto-Replies', icon: Bot },
    { key: 'contacts', label: 'Contacts', icon: Users },
    { key: 'broadcast', label: 'Broadcast', icon: Send },
    { key: 'scheduled', label: 'Scheduled', icon: Clock },
  ];

  return (
    <AdminLayout>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <Phone size={22} className="text-green-600" /> WhatsApp Manager
            </h1>
            <p className="text-xs text-slate-500 mt-0.5">Autonomous WhatsApp management — templates, flows, auto-replies, contacts & scheduling</p>
          </div>
        </div>

        <div className="flex gap-1 bg-slate-100 p-1 rounded-xl overflow-x-auto">
          {tabs.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
                tab === key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <Icon size={14} />
              {label}
            </button>
          ))}
        </div>

        {tab === 'dashboard' && <DashboardTab />}
        {tab === 'conversations' && <WhatsAppInbox />}
        {tab === 'templates' && <TemplatesTab />}
        {tab === 'flows' && <FlowsTab />}
        {tab === 'auto_replies' && <AutoRepliesTab />}
        {tab === 'contacts' && <ContactsTab />}
        {tab === 'broadcast' && <BroadcastTab />}
        {tab === 'scheduled' && <ScheduledTab />}
      </div>
    </AdminLayout>
  );
}

// ─── Dashboard Tab ────────────────────────────────────────────────

const DashboardTab = () => {
  const toast = useToast();
  const [balance, setBalance] = useState<any>(null);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [balanceLoading, setBalanceLoading] = useState(true);
  const [days, setDays] = useState(30);

  const loadBalance = useCallback(async () => {
    setBalanceLoading(true);
    try { setBalance(await getWaBalance()); } catch (e: any) { toast.show(e.message, 'error'); }
    finally { setBalanceLoading(false); }
  }, []);

  const loadStats = useCallback(async (d?: number) => {
    setLoading(true);
    try { setStats(await getWaStats(d || days)); } catch (e: any) { toast.show(e.message, 'error'); }
    finally { setLoading(false); }
  }, [days]);

  useEffect(() => { loadBalance(); loadStats(); }, []);

  const cs = stats?.contactStats;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-5 bg-gradient-to-br from-green-500 to-green-700 text-white">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2"><Wifi size={18} /><span className="text-sm font-semibold opacity-90">API Balance</span></div>
            <button onClick={loadBalance} disabled={balanceLoading} className="p-1.5 rounded-lg bg-white/20 hover:bg-white/30 transition">
              <RefreshCw size={14} className={balanceLoading ? 'animate-spin' : ''} />
            </button>
          </div>
          {balanceLoading ? <Loader2 className="animate-spin mx-auto my-4" size={24} /> :
           balance?.error ? <div className="text-sm opacity-90 flex items-center gap-2"><AlertTriangle size={16} /> {balance.error}</div> :
           <div>
             <div className="text-3xl font-bold mb-1">{balance?.data?.balance ?? balance?.data?.whatsapp_balance ?? 'N/A'}</div>
             <p className="text-xs opacity-75">Credits remaining</p>
           </div>}
        </Card>

        {stats?.totals && <>
          <Card className="p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-xl bg-blue-100"><ArrowUpRight size={18} className="text-blue-600" /></div>
              <div>
                <p className="text-slate-500 text-xs">Messages Sent</p>
                <h3 className="text-2xl font-bold text-slate-900">{stats.totals.total_sent}</h3>
              </div>
            </div>
            <div className="flex gap-3 text-xs">
              <span className="text-green-600"><CheckCircle size={12} className="inline mr-0.5" />{stats.totals.delivered + stats.totals.read} delivered</span>
              <span className="text-red-500"><XCircle size={12} className="inline mr-0.5" />{stats.totals.failed} failed</span>
            </div>
            <div className="mt-1 text-xs text-slate-400">Rate: <b className="text-slate-700">{stats.totals.delivery_rate}%</b></div>
          </Card>

          <Card className="p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-xl bg-purple-100"><ArrowDownLeft size={18} className="text-purple-600" /></div>
              <div>
                <p className="text-slate-500 text-xs">Received</p>
                <h3 className="text-2xl font-bold text-slate-900">{stats.totals.total_received}</h3>
              </div>
            </div>
            <div className="text-xs text-slate-500"><Users size={12} className="inline mr-0.5" />{stats.totals.unique_users} unique users</div>
          </Card>

          <Card className="p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-xl bg-green-100"><Settings size={18} className="text-green-600" /></div>
              <div>
                <p className="text-slate-500 text-xs">System</p>
                <h3 className="text-lg font-bold text-slate-900">Active</h3>
              </div>
            </div>
            {cs && <div className="space-y-0.5 text-xs text-slate-500">
              <div>{cs.total_contacts} contacts ({cs.opted_out} opted out)</div>
              <div>{cs.active_flows} flows, {cs.active_auto_replies} auto-replies</div>
              <div>{cs.pending_scheduled} scheduled pending</div>
            </div>}
          </Card>
        </>}
      </div>

      <div className="flex items-center gap-2">
        <span className="text-xs text-slate-500">Period:</span>
        {[7, 14, 30, 90].map(d => (
          <button key={d} onClick={() => { setDays(d); loadStats(d); }}
            className={`px-3 py-1 rounded-lg text-xs font-medium transition ${days === d ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
            {d}d
          </button>
        ))}
        <button onClick={() => loadStats()} className="ml-2 p-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 transition">
          <RefreshCw size={14} className={loading ? 'animate-spin text-slate-400' : 'text-slate-600'} />
        </button>
      </div>

      {stats?.daily && stats.daily.length > 0 && (
        <Card className="p-5">
          <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2"><TrendingUp size={16} /> Daily Volume</h3>
          <div className="flex items-end gap-1" style={{ height: 180 }}>
            {(() => {
              const maxVal = Math.max(...stats.daily.map((d: any) => Math.max(Number(d.sent) || 0, Number(d.received) || 0)), 1);
              return stats.daily.map((d: any, i: number) => {
                const sent = Number(d.sent) || 0;
                const received = Number(d.received) || 0;
                const dateLabel = new Date(d.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-0.5 group" title={`${dateLabel}\nSent: ${sent}\nReceived: ${received}`}>
                    <div className="flex gap-px items-end w-full justify-center" style={{ height: 160 }}>
                      <div className="bg-blue-400 rounded-t-sm" style={{ height: (sent / maxVal) * 160, width: '40%', minHeight: sent > 0 ? 2 : 0 }} />
                      <div className="bg-purple-400 rounded-t-sm" style={{ height: (received / maxVal) * 160, width: '40%', minHeight: received > 0 ? 2 : 0 }} />
                    </div>
                    <span className="text-[8px] text-slate-400 rotate-[-45deg] origin-top-left mt-1 whitespace-nowrap">{dateLabel}</span>
                  </div>
                );
              });
            })()}
          </div>
          <div className="flex items-center gap-4 mt-4 text-xs text-slate-500">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-blue-400" /> Sent</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-purple-400" /> Received</span>
          </div>
        </Card>
      )}

      {loading && !stats && <div className="flex justify-center py-12"><Loader2 className="animate-spin text-slate-400" size={32} /></div>}
    </div>
  );
};

// ─── Templates Tab ────────────────────────────────────────────────

const TemplatesTab = () => {
  const toast = useToast();
  const [templates, setTemplates] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<any>(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r: any = await getWaTemplates(); setTemplates(r.data?.templates || []); } catch (e: any) { toast.show(e.message, 'error'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      if (editing.id) {
        await updateWaTemplate(editing.id, editing);
        toast.show('Template updated', 'success');
      } else {
        await createWaTemplate(editing);
        toast.show('Template created', 'success');
      }
      setEditing(null);
      load();
    } catch (e: any) { toast.show(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Delete this template?')) return;
    try { await deleteWaTemplate(id); toast.show('Deleted', 'success'); load(); } catch (e: any) { toast.show(e.message, 'error'); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-slate-400" size={32} /></div>;

  if (editing !== null) {
    const e = editing;
    const set = (k: string, v: any) => setEditing({ ...e, [k]: v });
    const bodyText = e.body_text || '';
    const varMatches = bodyText.match(/\{\{\d+\}\}/g) || [];
    const varCount = new Set(varMatches).size;
    let preview = bodyText;
    const sampleVals = e.sample_values || [];
    sampleVals.forEach((v: string, i: number) => { preview = preview.replace(`{{${i + 1}}}`, v || `[var${i + 1}]`); });

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-900">{e.id ? 'Edit Template' : 'New Template'}</h2>
          <button onClick={() => setEditing(null)} className="p-2 rounded-lg hover:bg-slate-100"><X size={18} /></button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="p-5 space-y-3">
            <Input label="Template Name *" value={e.name || ''} onChange={ev => set('name', ev.target.value)} placeholder="e.g. welcome" />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Category</label>
                <select value={e.category || 'utility'} onChange={ev => set('category', ev.target.value)} className="w-full text-sm border rounded-lg px-3 py-2">
                  <option value="utility">Utility</option><option value="marketing">Marketing</option><option value="authentication">Authentication</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Status</label>
                <select value={e.status || 'draft'} onChange={ev => set('status', ev.target.value)} className="w-full text-sm border rounded-lg px-3 py-2">
                  <option value="draft">Draft</option><option value="pending">Pending Approval</option><option value="approved">Approved</option><option value="rejected">Rejected</option><option value="paused">Paused</option>
                </select>
              </div>
            </div>
            <Input label="AuthKey WID" value={e.wid || ''} onChange={ev => set('wid', ev.target.value)} placeholder="Template WID from AuthKey console" />
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Header Type</label>
              <select value={e.header_type || 'none'} onChange={ev => set('header_type', ev.target.value)} className="w-full text-sm border rounded-lg px-3 py-2">
                <option value="none">None</option><option value="text">Text</option><option value="image">Image</option><option value="video">Video</option><option value="document">Document</option>
              </select>
            </div>
            {e.header_type && e.header_type !== 'none' && (
              <Input label="Header Content" value={e.header_content || ''} onChange={ev => set('header_content', ev.target.value)} placeholder={e.header_type === 'text' ? 'Header text' : 'Media URL'} />
            )}
            <Input label="Body Text *" multiline value={e.body_text || ''} onChange={ev => set('body_text', ev.target.value)} rows={5} placeholder="Hello {{1}}, your meeting is scheduled for {{2}}." helper={`${varCount} variable${varCount !== 1 ? 's' : ''} detected`} />
            <Input label="Footer Text" value={e.footer_text || ''} onChange={ev => set('footer_text', ev.target.value)} />
            {varCount > 0 && (
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Variable Labels & Samples</label>
                <div className="space-y-2">
                  {Array.from({ length: varCount }, (_, i) => (
                    <div key={i} className="grid grid-cols-2 gap-2">
                      <input placeholder={`Label for {{${i + 1}}}`} value={(e.variable_labels || [])[i] || ''} onChange={ev => { const l = [...(e.variable_labels || [])]; l[i] = ev.target.value; set('variable_labels', l); }} className="text-sm border rounded-lg px-3 py-1.5" />
                      <input placeholder="Sample value" value={(e.sample_values || [])[i] || ''} onChange={ev => { const s = [...(e.sample_values || [])]; s[i] = ev.target.value; set('sample_values', s); }} className="text-sm border rounded-lg px-3 py-1.5" />
                    </div>
                  ))}
                </div>
              </div>
            )}
            <Input label="Tags" value={e.tags || ''} onChange={ev => set('tags', ev.target.value)} placeholder="hr,notification,welcome" />
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={e.is_active !== false} onChange={ev => set('is_active', ev.target.checked)} id="tpl_active" />
              <label htmlFor="tpl_active" className="text-sm text-slate-700">Active</label>
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={handleSave} isLoading={saving} disabled={!e.name || !e.body_text} className="flex-1">{e.id ? 'Update' : 'Create'} Template</Button>
              <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            </div>
          </Card>
          <Card className="p-5">
            <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><Eye size={16} /> Preview</h3>
            <div className="bg-[#e5ddd5] rounded-xl p-4">
              <div className="bg-white rounded-xl p-4 max-w-xs shadow-sm">
                {e.header_type === 'text' && e.header_content && <p className="font-bold text-sm mb-1">{e.header_content}</p>}
                {e.header_type === 'image' && e.header_content && <div className="bg-slate-200 rounded-lg h-32 mb-2 flex items-center justify-center text-xs text-slate-400">Image Header</div>}
                <p className="text-sm whitespace-pre-wrap">{preview || 'Template body will appear here...'}</p>
                {e.footer_text && <p className="text-xs text-slate-400 mt-2">{e.footer_text}</p>}
              </div>
            </div>
            {e.wid && (
              <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500"><b>WID:</b> <code className="bg-white px-1 rounded">{e.wid}</code></p>
                <p className="text-xs text-slate-500 mt-1"><b>Category:</b> {e.category || 'utility'}</p>
                <p className="text-xs text-slate-500 mt-1"><b>Language:</b> {e.language || 'en'}</p>
              </div>
            )}
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-700">WhatsApp Templates ({templates.length})</h2>
        <Button size="sm" onClick={() => setEditing({ is_active: true, category: 'utility', status: 'draft', header_type: 'none', language: 'en', variable_labels: [], sample_values: [], buttons: [] })} leftIcon={<Plus size={14} />}>New Template</Button>
      </div>
      <div className="space-y-3">
        {templates.map((t: any) => (
          <Card key={t.id || t.name} className={`p-4 ${!t.is_active ? 'opacity-60' : ''}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-sm text-slate-800">{t.name}</span>
                <Badge variant={t.status === 'approved' ? 'success' : 'default'}>{t.status || (t.configured ? 'approved' : 'draft')}</Badge>
                {t.source === 'env' && <Badge variant="warning">ENV</Badge>}
                <Badge>{t.category || 'utility'}</Badge>
              </div>
              <div className="flex items-center gap-1">
                {t.wid && <span className="font-mono text-xs text-slate-400 mr-2">WID: {t.wid}</span>}
                {t.source !== 'env' && t.id && <>
                  <button onClick={() => setEditing({ ...t, variable_labels: t.variable_labels || [], sample_values: t.sample_values || [], buttons: t.buttons || [] })} className="p-1.5 rounded-lg hover:bg-slate-100"><Edit3 size={14} className="text-slate-500" /></button>
                  <button onClick={() => handleDelete(t.id)} className="p-1.5 rounded-lg hover:bg-red-50"><Trash2 size={14} className="text-red-400" /></button>
                </>}
              </div>
            </div>
            <p className="text-xs text-slate-500 line-clamp-2">{t.body_text || t.description}</p>
            <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
              <span>Vars: {t.variable_count ?? t.vars ?? 0}</span>
              {t.usage_count > 0 && <span>Used: {t.usage_count}x</span>}
              {t.last_used_at && <span>Last: {new Date(t.last_used_at).toLocaleDateString()}</span>}
            </div>
          </Card>
        ))}
        {templates.length === 0 && <p className="text-center text-slate-400 text-sm py-8">No templates yet. Create one to get started.</p>}
      </div>
    </div>
  );
};

// ─── Flows Tab ────────────────────────────────────────────────────

const FlowsTab = () => {
  const toast = useToast();
  const [flows, setFlows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingFlow, setEditingFlow] = useState<any>(null);
  const [editingSteps, setEditingSteps] = useState<any[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [expandedFlow, setExpandedFlow] = useState<number | null>(null);
  const [flowSteps, setFlowSteps] = useState<any[]>([]);

  const load = async () => {
    setLoading(true);
    try { const r: any = await getWaFlows(); setFlows(r.data?.flows || []); } catch (e: any) { toast.show(e.message, 'error'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const loadSteps = async (flowId: number) => {
    if (expandedFlow === flowId) { setExpandedFlow(null); return; }
    try { const r: any = await getWaFlow(flowId); setFlowSteps(r.data?.steps || []); setExpandedFlow(flowId); }
    catch (e: any) { toast.show(e.message, 'error'); }
  };

  const handleSeedDefaults = async () => {
    try { const r: any = await seedWaDefaultFlows(); toast.show(r.message, 'success'); load(); }
    catch (e: any) { toast.show(e.message, 'error'); }
  };

  const handleSaveFlow = async () => {
    if (!editingFlow) return;
    setSaving(true);
    try {
      if (editingFlow.id) {
        await updateWaFlow(editingFlow.id, editingFlow);
      } else {
        const r: any = await createWaFlow(editingFlow);
        if (editingSteps && r.data?.flow?.id) {
          await saveWaFlowSteps(r.data.flow.id, editingSteps);
        }
      }
      toast.show('Flow saved', 'success');
      setEditingFlow(null);
      setEditingSteps(null);
      load();
    } catch (e: any) { toast.show(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const handleSaveSteps = async (flowId: number, steps: any[]) => {
    setSaving(true);
    try { const r: any = await saveWaFlowSteps(flowId, steps); setFlowSteps(r.data?.steps || []); toast.show('Steps saved', 'success'); }
    catch (e: any) { toast.show(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const handleDeleteFlow = async (id: number) => {
    if (!confirm('Delete this flow and all its steps?')) return;
    try { await deleteWaFlow(id); toast.show('Flow deleted', 'success'); load(); } catch (e: any) { toast.show(e.message, 'error'); }
  };

  const handleToggleFlow = async (flow: any) => {
    try { await updateWaFlow(flow.id, { is_active: !flow.is_active }); toast.show(flow.is_active ? 'Flow paused' : 'Flow activated', 'success'); load(); }
    catch (e: any) { toast.show(e.message, 'error'); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-slate-400" size={32} /></div>;

  if (editingFlow) {
    const e = editingFlow;
    const set = (k: string, v: any) => setEditingFlow({ ...e, [k]: v });
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold">{e.id ? 'Edit Flow' : 'New Flow'}</h2>
          <button onClick={() => { setEditingFlow(null); setEditingSteps(null); }} className="p-2 rounded-lg hover:bg-slate-100"><X size={18} /></button>
        </div>
        <Card className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Input label="Name *" value={e.name || ''} onChange={ev => set('name', ev.target.value)} />
            <Input label="Slug *" value={e.slug || ''} onChange={ev => set('slug', ev.target.value)} placeholder="student_onboarding" />
          </div>
          <Input label="Description" value={e.description || ''} onChange={ev => set('description', ev.target.value)} />
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Trigger</label>
              <select value={e.trigger_type || 'new_user'} onChange={ev => set('trigger_type', ev.target.value)} className="w-full text-sm border rounded-lg px-3 py-2">
                <option value="new_user">New User</option><option value="keyword">Keyword</option><option value="menu_select">Menu Select</option><option value="manual">Manual</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Target Role</label>
              <select value={e.target_role || 'any'} onChange={ev => set('target_role', ev.target.value)} className="w-full text-sm border rounded-lg px-3 py-2">
                <option value="any">Any</option><option value="employer">Employer</option><option value="chef">Chef</option>
              </select>
            </div>
            <Input label="Priority" type="number" value={e.priority || 0} onChange={ev => set('priority', parseInt(ev.target.value))} />
          </div>
          {e.trigger_type === 'keyword' && <Input label="Trigger Keyword" value={e.trigger_value || ''} onChange={ev => set('trigger_value', ev.target.value)} />}
          <Input label="Start Step Slug" value={e.start_step || 'start'} onChange={ev => set('start_step', ev.target.value)} />
          <div className="flex items-center gap-2">
            <input type="checkbox" checked={e.is_active !== false} onChange={ev => set('is_active', ev.target.checked)} id="flow_active" />
            <label htmlFor="flow_active" className="text-sm">Active</label>
          </div>
          <Button onClick={handleSaveFlow} isLoading={saving} disabled={!e.name || !e.slug}>{e.id ? 'Update' : 'Create'} Flow</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-700">Chatbot Flows ({flows.length})</h2>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={handleSeedDefaults} leftIcon={<Zap size={12} />}>Seed Defaults</Button>
          <Button size="sm" onClick={() => setEditingFlow({ is_active: true, trigger_type: 'new_user', target_role: 'any', priority: 0, start_step: 'start' })} leftIcon={<Plus size={14} />}>New Flow</Button>
        </div>
      </div>
      <div className="space-y-3">
        {flows.map((f: any) => (
          <Card key={f.id} className={`${!f.is_active ? 'opacity-60' : ''}`}>
            <div className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <button onClick={() => loadSteps(f.id)} className="p-0.5">{expandedFlow === f.id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</button>
                  <GitBranch size={16} className="text-green-600" />
                  <span className="font-semibold text-sm">{f.name}</span>
                  <Badge variant={f.is_active ? 'success' : 'default'}>{f.is_active ? 'Active' : 'Paused'}</Badge>
                  <Badge>{f.trigger_type}</Badge>
                  <Badge>{f.target_role}</Badge>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => handleToggleFlow(f)} className="p-1.5 rounded-lg hover:bg-slate-100">{f.is_active ? <PauseCircle size={14} className="text-amber-500" /> : <PlayCircle size={14} className="text-green-500" />}</button>
                  <button onClick={() => setEditingFlow(f)} className="p-1.5 rounded-lg hover:bg-slate-100"><Edit3 size={14} className="text-slate-500" /></button>
                  <button onClick={() => handleDeleteFlow(f.id)} className="p-1.5 rounded-lg hover:bg-red-50"><Trash2 size={14} className="text-red-400" /></button>
                </div>
              </div>
              {f.description && <p className="text-xs text-slate-500 ml-7">{f.description}</p>}
              <div className="flex items-center gap-3 mt-1 ml-7 text-xs text-slate-400">
                <span>{f.step_count || 0} steps</span><span>Slug: <code className="bg-slate-100 px-1 rounded">{f.slug}</code></span><span>Priority: {f.priority}</span>
              </div>
            </div>
            {expandedFlow === f.id && (
              <FlowStepsEditor flowId={f.id} steps={flowSteps} onSave={(steps) => handleSaveSteps(f.id, steps)} saving={saving} />
            )}
          </Card>
        ))}
        {flows.length === 0 && (
          <Card className="p-8 text-center">
            <GitBranch size={40} className="mx-auto mb-3 text-slate-300" />
            <p className="text-sm text-slate-500 mb-3">No flows configured yet.</p>
            <Button size="sm" onClick={handleSeedDefaults} leftIcon={<Zap size={14} />}>Seed Default Onboarding Flows</Button>
          </Card>
        )}
      </div>
    </div>
  );
};

const FlowStepsEditor = ({ flowId, steps: initialSteps, onSave, saving }: { flowId: number; steps: any[]; onSave: (steps: any[]) => void; saving: boolean }) => {
  const [steps, setSteps] = useState<any[]>(initialSteps);
  const [dirty, setDirty] = useState(false);

  useEffect(() => { setSteps(initialSteps); setDirty(false); }, [initialSteps]);

  const updateStep = (idx: number, field: string, value: any) => {
    const updated = [...steps];
    updated[idx] = { ...updated[idx], [field]: value };
    setSteps(updated);
    setDirty(true);
  };

  const addStep = () => {
    setSteps([...steps, { slug: `step_${steps.length}`, label: '', order_index: steps.length, message: '', input_type: 'text', options: [], save_as: '', next_step: '', conditional_next: [], validation: {}, is_active: true }]);
    setDirty(true);
  };

  const removeStep = (idx: number) => { setSteps(steps.filter((_, i) => i !== idx)); setDirty(true); };

  return (
    <div className="border-t px-4 pb-4 pt-3 bg-slate-50/50">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-xs font-semibold text-slate-600">Flow Steps ({steps.length})</h4>
        <div className="flex gap-2">
          <button onClick={addStep} className="px-2 py-1 text-xs rounded-lg border hover:bg-white flex items-center gap-1"><Plus size={12} /> Add Step</button>
          {dirty && <button onClick={() => onSave(steps)} disabled={saving} className="px-3 py-1 text-xs rounded-lg bg-green-600 text-white hover:bg-green-700 flex items-center gap-1">{saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save Steps</button>}
        </div>
      </div>
      <div className="space-y-3">
        {steps.map((s, i) => (
          <div key={i} className="bg-white rounded-xl border p-3 space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold text-slate-400 bg-slate-100 rounded px-1.5 py-0.5">#{i + 1}</span>
                <input value={s.slug || ''} onChange={e => updateStep(i, 'slug', e.target.value)} className="text-xs font-mono border rounded px-2 py-1 w-28" placeholder="step_slug" />
                <input value={s.label || ''} onChange={e => updateStep(i, 'label', e.target.value)} className="text-xs border rounded px-2 py-1 w-32" placeholder="Step Label" />
              </div>
              <button onClick={() => removeStep(i)} className="p-1 rounded hover:bg-red-50"><Trash2 size={12} className="text-red-400" /></button>
            </div>
            <textarea value={s.message || ''} onChange={e => updateStep(i, 'message', e.target.value)} rows={2} className="w-full text-xs border rounded-lg px-2 py-1.5" placeholder="Message to send..." />
            <div className="grid grid-cols-4 gap-2">
              <div>
                <label className="text-[10px] text-slate-500">Input Type</label>
                <select value={s.input_type || 'text'} onChange={e => updateStep(i, 'input_type', e.target.value)} className="w-full text-xs border rounded px-2 py-1">
                  <option value="text">Text</option><option value="option">Option</option><option value="phone">Phone</option><option value="email">Email</option><option value="number">Number</option><option value="skip">Skip</option>
                </select>
              </div>
              <div><label className="text-[10px] text-slate-500">Save As</label><input value={s.save_as || ''} onChange={e => updateStep(i, 'save_as', e.target.value)} className="w-full text-xs border rounded px-2 py-1 font-mono" placeholder="field_name" /></div>
              <div><label className="text-[10px] text-slate-500">Next Step</label><input value={s.next_step || ''} onChange={e => updateStep(i, 'next_step', e.target.value)} className="w-full text-xs border rounded px-2 py-1 font-mono" placeholder="next_slug" /></div>
              <div><label className="text-[10px] text-slate-500">Active</label><div className="pt-1"><input type="checkbox" checked={s.is_active !== false} onChange={e => updateStep(i, 'is_active', e.target.checked)} /></div></div>
            </div>
            {s.input_type === 'option' && (
              <div>
                <label className="text-[10px] text-slate-500">Options JSON</label>
                <textarea value={typeof s.options === 'string' ? s.options : JSON.stringify(s.options || [], null, 2)} onChange={e => { try { updateStep(i, 'options', JSON.parse(e.target.value)); } catch { updateStep(i, 'options', e.target.value); } }} rows={3} className="w-full text-xs border rounded-lg px-2 py-1.5 font-mono" />
              </div>
            )}
          </div>
        ))}
      </div>
      {steps.length === 0 && <p className="text-center text-xs text-slate-400 py-4">No steps. Add steps to define the flow.</p>}
    </div>
  );
};

// ─── Auto-Replies Tab ─────────────────────────────────────────────

const AutoRepliesTab = () => {
  const toast = useToast();
  const [rules, setRules] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<any>(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r: any = await getWaAutoReplies(); setRules(r.data?.rules || []); } catch (e: any) { toast.show(e.message, 'error'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      if (editing.id) { await updateWaAutoReply(editing.id, editing); }
      else { await createWaAutoReply(editing); }
      toast.show('Rule saved', 'success'); setEditing(null); load();
    } catch (e: any) { toast.show(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Delete this rule?')) return;
    try { await deleteWaAutoReply(id); toast.show('Deleted', 'success'); load(); } catch (e: any) { toast.show(e.message, 'error'); }
  };

  const handleToggle = async (rule: any) => {
    try { await updateWaAutoReply(rule.id, { is_active: !rule.is_active }); toast.show(rule.is_active ? 'Rule paused' : 'Rule activated', 'success'); load(); }
    catch (e: any) { toast.show(e.message, 'error'); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-slate-400" size={32} /></div>;

  if (editing) {
    const e = editing;
    const set = (k: string, v: any) => setEditing({ ...e, [k]: v });
    const kws = Array.isArray(e.keywords) ? e.keywords : [];
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold">{e.id ? 'Edit Rule' : 'New Auto-Reply Rule'}</h2>
          <button onClick={() => setEditing(null)} className="p-2 rounded-lg hover:bg-slate-100"><X size={18} /></button>
        </div>
        <Card className="p-5 space-y-3">
          <Input label="Rule Name *" value={e.name || ''} onChange={ev => set('name', ev.target.value)} placeholder="e.g. Greeting Response" />
          <Input label="Keywords *" multiline value={kws.join('\n')} onChange={ev => set('keywords', ev.target.value.split('\n').filter(Boolean))} rows={3} placeholder={"hi\nhello\nhey"} helper="One per line" />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Match Type</label>
              <select value={e.match_type || 'contains'} onChange={ev => set('match_type', ev.target.value)} className="w-full text-sm border rounded-lg px-3 py-2">
                <option value="exact">Exact Match</option><option value="contains">Contains</option><option value="starts_with">Starts With</option><option value="regex">Regex</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Target Audience</label>
              <select value={e.target_audience || 'all'} onChange={ev => set('target_audience', ev.target.value)} className="w-full text-sm border rounded-lg px-3 py-2">
                <option value="all">All Users</option><option value="new">New (unregistered)</option><option value="existing">Existing (registered)</option>
              </select>
            </div>
          </div>
          <Input label="Response Message *" multiline value={e.response_message || ''} onChange={ev => set('response_message', ev.target.value)} rows={4} placeholder="Hello! Welcome to EcoleMinds. How can I help you today?" helper="Use {phone} to insert the caller's phone number." />
          <Input label="Priority" type="number" value={e.priority || 0} onChange={ev => set('priority', parseInt(ev.target.value))} helper="Higher = checked first" />
          <div className="flex items-center gap-2">
            <input type="checkbox" checked={e.is_active !== false} onChange={ev => set('is_active', ev.target.checked)} id="ar_active" />
            <label htmlFor="ar_active" className="text-sm">Active</label>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSave} isLoading={saving} disabled={!e.name || kws.length === 0 || !e.response_message}>{e.id ? 'Update' : 'Create'} Rule</Button>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-700">Auto-Reply Rules ({rules.length})</h2>
        <Button size="sm" onClick={() => setEditing({ is_active: true, match_type: 'contains', target_audience: 'all', priority: 0, keywords: [] })} leftIcon={<Plus size={14} />}>New Rule</Button>
      </div>
      <div className="space-y-3">
        {rules.map((r: any) => (
          <Card key={r.id} className={`p-4 ${!r.is_active ? 'opacity-60' : ''}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Bot size={16} className="text-green-600" />
                <span className="font-semibold text-sm">{r.name}</span>
                <Badge variant={r.is_active ? 'success' : 'default'}>{r.is_active ? 'Active' : 'Paused'}</Badge>
                <Badge>{r.match_type}</Badge>
                <Badge>{r.target_audience}</Badge>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-xs text-slate-400 mr-2">Triggered: {r.trigger_count || 0}x</span>
                <button onClick={() => handleToggle(r)} className="p-1.5 rounded-lg hover:bg-slate-100">{r.is_active ? <PauseCircle size={14} className="text-amber-500" /> : <PlayCircle size={14} className="text-green-500" />}</button>
                <button onClick={() => setEditing({ ...r, keywords: r.keywords || [] })} className="p-1.5 rounded-lg hover:bg-slate-100"><Edit3 size={14} className="text-slate-500" /></button>
                <button onClick={() => handleDelete(r.id)} className="p-1.5 rounded-lg hover:bg-red-50"><Trash2 size={14} className="text-red-400" /></button>
              </div>
            </div>
            <div className="flex flex-wrap gap-1 mb-1">
              {(r.keywords || []).map((k: string, i: number) => (
                <span key={i} className="text-[10px] px-1.5 py-0.5 bg-green-50 text-green-700 rounded-full border border-green-200">{k}</span>
              ))}
            </div>
            <p className="text-xs text-slate-500 line-clamp-2">{r.response_message}</p>
          </Card>
        ))}
        {rules.length === 0 && (
          <Card className="p-8 text-center">
            <Bot size={40} className="mx-auto mb-3 text-slate-300" />
            <p className="text-sm text-slate-500">No auto-reply rules. Create one to auto-respond to common messages.</p>
          </Card>
        )}
      </div>
    </div>
  );
};

// ─── Contacts Tab ─────────────────────────────────────────────────

const ContactsTab = () => {
  const toast = useToast();
  const [contacts, setContacts] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [allTags, setAllTags] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [selected, setSelected] = useState<number[]>([]);
  const [bulkTag, setBulkTag] = useState('');
  const [editingContact, setEditingContact] = useState<any>(null);

  const load = useCallback(async (p?: number) => {
    setLoading(true);
    try {
      const params: any = { page: p || page, limit: 50 };
      if (search) params.search = search;
      if (tagFilter) params.tag = tagFilter;
      if (typeFilter) params.user_type = typeFilter;
      const r: any = await getWaContacts(params);
      setContacts(r.data?.contacts || []);
      setTotal(r.data?.total || 0);
      setPages(r.data?.pages || 1);
      setAllTags(r.data?.tags || {});
    } catch (e: any) { toast.show(e.message, 'error'); }
    finally { setLoading(false); }
  }, [page, search, tagFilter, typeFilter]);

  useEffect(() => { load(1); }, [search, tagFilter, typeFilter]);

  const handleBulkTag = async (action: 'add' | 'remove') => {
    if (!bulkTag || selected.length === 0) return;
    try { const r: any = await bulkTagWaContacts({ ids: selected, tag: bulkTag, action }); toast.show(`${action === 'add' ? 'Added' : 'Removed'} tag for ${r.updated} contacts`, 'success'); setSelected([]); setBulkTag(''); load(); }
    catch (e: any) { toast.show(e.message, 'error'); }
  };

  const handleUpdateContact = async () => {
    if (!editingContact) return;
    try { await updateWaContact(editingContact.id, editingContact); toast.show('Contact updated', 'success'); setEditingContact(null); load(); }
    catch (e: any) { toast.show(e.message, 'error'); }
  };

  const toggleSelect = (id: number) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-sm font-semibold text-slate-700">Contacts ({total})</h2>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="pl-8 pr-3 py-1.5 text-xs border rounded-lg w-48" />
          </div>
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="text-xs border rounded-lg px-2 py-1.5">
            <option value="">All Types</option><option value="student">Students</option><option value="unknown">Unknown</option>
          </select>
          {Object.keys(allTags).length > 0 && (
            <select value={tagFilter} onChange={e => setTagFilter(e.target.value)} className="text-xs border rounded-lg px-2 py-1.5">
              <option value="">All Tags</option>
              {Object.entries(allTags).map(([tag, count]) => <option key={tag} value={tag}>{tag} ({count})</option>)}
            </select>
          )}
        </div>
      </div>

      {selected.length > 0 && (
        <Card className="p-3 bg-blue-50 border-blue-200">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-medium text-blue-700">{selected.length} selected</span>
            <input value={bulkTag} onChange={e => setBulkTag(e.target.value)} placeholder="Tag name" className="text-xs border rounded px-2 py-1 w-32" />
            <button onClick={() => handleBulkTag('add')} disabled={!bulkTag} className="px-2 py-1 text-xs rounded bg-green-600 text-white disabled:opacity-50">+ Add Tag</button>
            <button onClick={() => handleBulkTag('remove')} disabled={!bulkTag} className="px-2 py-1 text-xs rounded bg-red-500 text-white disabled:opacity-50">- Remove Tag</button>
            <button onClick={() => setSelected([])} className="px-2 py-1 text-xs rounded border">Clear</button>
          </div>
        </Card>
      )}

      {editingContact && (
        <Card className="p-4 border-2 border-green-300">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold">Edit Contact: {editingContact.phone}</h3>
            <button onClick={() => setEditingContact(null)}><X size={16} /></button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Name" value={editingContact.name || ''} onChange={e => setEditingContact({ ...editingContact, name: e.target.value })} />
            <Input label="Tags (comma-separated)" value={editingContact.tags || ''} onChange={e => setEditingContact({ ...editingContact, tags: e.target.value })} />
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Status</label>
              <select value={editingContact.status || 'active'} onChange={e => setEditingContact({ ...editingContact, status: e.target.value })} className="w-full text-xs border rounded px-2 py-1.5">
                <option value="active">Active</option><option value="inactive">Inactive</option><option value="blocked">Blocked</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Opted In</label>
              <div className="pt-1"><input type="checkbox" checked={editingContact.is_opt_in || editingContact.opted_in} onChange={e => setEditingContact({ ...editingContact, is_opt_in: e.target.checked, opted_in: e.target.checked })} /> WhatsApp opt-in</div>
            </div>
            <div className="col-span-2">
              <Input label="Notes" multiline value={editingContact.notes || ''} onChange={e => setEditingContact({ ...editingContact, notes: e.target.value })} rows={2} />
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <Button size="sm" onClick={handleUpdateContact} leftIcon={<Save size={12} />}>Save</Button>
            <Button size="sm" variant="ghost" onClick={() => setEditingContact(null)}>Cancel</Button>
          </div>
        </Card>
      )}

      {loading ? <div className="flex justify-center py-8"><Loader2 className="animate-spin text-slate-400" size={24} /></div> :
      <div className="space-y-1">
        {contacts.map((c: any) => (
          <div key={c.id} className={`flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-50 ${!c.is_opt_in && !c.opted_in ? 'opacity-50' : ''}`}>
            <input type="checkbox" checked={selected.includes(c.id)} onChange={() => toggleSelect(c.id)} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium truncate">{c.name || c.phone}</span>
                <span className={`text-[8px] px-1 py-0.5 rounded font-bold ${c.user_type === 'student' ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-500'}`}>{c.user_type?.toUpperCase() || '?'}</span>
                {!(c.is_opt_in || c.opted_in) && <Badge variant="error">Opted Out</Badge>}
                {c.status === 'blocked' && <Badge variant="error">Blocked</Badge>}
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <span>{c.phone}</span>
                {c.tags && c.tags.split(',').filter(Boolean).map((t: string) => <span key={t} className="text-[9px] px-1 py-0.5 bg-slate-100 rounded">{t.trim()}</span>)}
              </div>
            </div>
            <div className="text-right text-xs text-slate-400 flex-shrink-0">
              <div>{c.total_messages_sent || 0} sent / {c.total_messages_received || 0} recv</div>
              {c.last_contacted_at && <div>{new Date(c.last_contacted_at).toLocaleDateString()}</div>}
            </div>
            <button onClick={() => setEditingContact({ ...c })} className="p-1.5 rounded-lg hover:bg-slate-100"><Edit3 size={14} className="text-slate-400" /></button>
          </div>
        ))}
        {contacts.length === 0 && <p className="text-center text-slate-400 text-sm py-8">No contacts found.</p>}
      </div>}

      {pages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button disabled={page <= 1} onClick={() => { setPage(page - 1); load(page - 1); }} className="px-3 py-1 text-xs rounded border disabled:opacity-50">Prev</button>
          <span className="text-xs text-slate-500">{page} / {pages}</span>
          <button disabled={page >= pages} onClick={() => { setPage(page + 1); load(page + 1); }} className="px-3 py-1 text-xs rounded border disabled:opacity-50">Next</button>
        </div>
      )}
    </div>
  );
};

// ─── Broadcast Tab ────────────────────────────────────────────────

const BroadcastTab = () => {
  const toast = useToast();
  const [templates, setTemplates] = useState<any[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [numbersText, setNumbersText] = useState('');
  const [bodyValues, setBodyValues] = useState<Record<string, string>>({});
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [useTag, setUseTag] = useState(false);
  const [tag, setTag] = useState('');
  const [tagContacts, setTagContacts] = useState<any>(null);

  useEffect(() => {
    (async () => {
      try { const r: any = await getWaTemplates(); setTemplates((r.data?.templates || []).filter((t: any) => (t.wid || t.configured) && (t.status === 'approved' || t.configured))); }
      catch (e: any) { toast.show(e.message, 'error'); }
      finally { setLoadingTemplates(false); }
    })();
  }, []);

  const loadTagContacts = async (t: string) => {
    if (!t) { setTagContacts(null); return; }
    try { const r: any = await getWaContactsByTag(t); setTagContacts(r.data); } catch { setTagContacts(null); }
  };

  const numbers = numbersText.split(/[,\n\r]+/).map(n => n.trim().replace(/[^0-9]/g, '')).filter(n => n.length >= 10);
  const selectedMeta = templates.find((t: any) => (t.wid || t.id) === selectedTemplate);
  const varCount = selectedMeta?.variable_count ?? selectedMeta?.vars ?? 0;

  const handleSend = async () => {
    const targetCount = useTag ? (tagContacts?.count || 0) : numbers.length;
    if (!selectedTemplate || targetCount === 0) { toast.show('Select a template and specify recipients', 'error'); return; }
    if (!confirm(`Send broadcast to ${targetCount} recipient(s)?`)) return;
    setSending(true); setResult(null);
    try {
      const data: any = { templateId: selectedTemplate, bodyValues: Object.keys(bodyValues).length > 0 ? bodyValues : undefined };
      if (useTag) { data.tag = tag; data.numbers = []; } else { data.numbers = numbers; }
      const r: any = await sendWaBroadcast(data);
      setResult(r.data);
      if (r.data?.success) { toast.show(`Broadcast sent to ${r.data?.total_sent} numbers`, 'success'); setNumbersText(''); }
      else toast.show(`${r.data?.total_failed} failed`, 'error');
    } catch (e: any) { toast.show(e.message, 'error'); }
    finally { setSending(false); }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Card className="p-5 space-y-4">
        <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><Send size={16} /> Compose Broadcast</h3>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Template</label>
          {loadingTemplates ? <Loader2 className="animate-spin" size={16} /> : (
            <select value={selectedTemplate} onChange={e => { setSelectedTemplate(e.target.value); setBodyValues({}); }} className="w-full text-sm border rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-green-300">
              <option value="">-- Select Template --</option>
              {templates.map((t: any) => <option key={t.wid || t.id} value={t.wid || t.id}>{t.name} {t.wid ? `(WID: ${t.wid})` : ''}</option>)}
            </select>
          )}
        </div>
        {selectedMeta && varCount > 0 && (
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Template Variables</label>
            <div className="space-y-2">
              {Array.from({ length: varCount }, (_, i) => (
                <input key={i} type="text" placeholder={`Variable {{${i + 1}}}${selectedMeta.variable_labels?.[i] ? ` — ${selectedMeta.variable_labels[i]}` : ''}`} value={bodyValues[String(i + 1)] || ''} onChange={e => setBodyValues(prev => ({ ...prev, [String(i + 1)]: e.target.value }))} className="w-full text-sm border rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-green-300" />
              ))}
            </div>
          </div>
        )}
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-xs cursor-pointer"><input type="radio" checked={!useTag} onChange={() => setUseTag(false)} /> Phone Numbers</label>
          <label className="flex items-center gap-1.5 text-xs cursor-pointer"><input type="radio" checked={useTag} onChange={() => setUseTag(true)} /> By Contact Tag</label>
        </div>
        {useTag ? (
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Contact Tag</label>
            <input value={tag} onChange={e => { setTag(e.target.value); loadTagContacts(e.target.value); }} placeholder="e.g. mumbai, vip, active" className="w-full text-sm border rounded-lg px-3 py-2" />
            {tagContacts && <p className="text-xs text-green-600 mt-1">{tagContacts.count} opted-in contacts with this tag</p>}
          </div>
        ) : (
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Phone Numbers <span className="text-slate-400">(comma or newline separated)</span></label>
            <textarea value={numbersText} onChange={e => setNumbersText(e.target.value)} rows={6} placeholder={"9876543210\n8765432109"} className="w-full text-sm border rounded-lg px-3 py-2 font-mono" />
            <p className="text-xs text-slate-400 mt-1">{numbers.length} valid numbers</p>
          </div>
        )}
        <Button onClick={handleSend} isLoading={sending} disabled={!selectedTemplate || (useTag ? !tag : numbers.length === 0)} className="w-full">
          {sending ? 'Sending...' : <><Zap size={14} className="mr-2" /> Send Broadcast ({useTag ? (tagContacts?.count || 0) : numbers.length})</>}
        </Button>
      </Card>
      <Card className="p-5">
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Result</h3>
        {result ? (
          <div className={`p-4 rounded-xl ${result.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            <div className="flex items-center gap-2 mb-2">
              {result.success ? <CheckCircle size={18} className="text-green-600" /> : <XCircle size={18} className="text-red-600" />}
              <span className="font-semibold text-sm">{result.success ? 'Sent!' : 'Some Failed'}</span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="bg-white rounded-lg p-2 text-center"><div className="text-lg font-bold text-green-600">{result.total_sent}</div><div className="text-slate-500">Sent</div></div>
              <div className="bg-white rounded-lg p-2 text-center"><div className="text-lg font-bold text-red-600">{result.total_failed}</div><div className="text-slate-500">Failed</div></div>
            </div>
          </div>
        ) : (
          <div className="text-center py-12 text-slate-400">
            <Send size={40} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm">Send a broadcast to see results</p>
          </div>
        )}
        <div className="mt-4 p-3 bg-slate-50 rounded-lg">
          <h4 className="text-xs font-semibold text-slate-600 mb-2">Tips</h4>
          <ul className="text-xs text-slate-500 space-y-1">
            <li>- Max 200 per batch (auto-split)</li>
            <li>- Opted-out contacts are auto-excluded</li>
            <li>- Use tags to target segments</li>
            <li>- Templates must be approved in AuthKey</li>
          </ul>
        </div>
      </Card>
    </div>
  );
};

// ─── Scheduled Tab ────────────────────────────────────────────────

const ScheduledTab = () => {
  const toast = useToast();
  const [scheduled, setScheduled] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [templates, setTemplates] = useState<any[]>([]);
  const [form, setForm] = useState<any>({ type: 'single', phone: '', message: '', template_id: '', template_values: {}, recipients: '', recipient_tag: '', scheduled_at: '' });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r: any = await getWaScheduled(); setScheduled(r.data?.scheduled || []); } catch (e: any) { toast.show(e.message, 'error'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    (async () => {
      try { const r: any = await getWaTemplates(); setTemplates((r.data?.templates || []).filter((t: any) => t.wid && (t.status === 'approved' || t.configured))); } catch {}
    })();
  }, []);

  const handleCreate = async () => {
    if (!form.scheduled_at) { toast.show('Schedule date/time required', 'error'); return; }
    setSaving(true);
    try {
      const data: any = { type: form.type, scheduled_at: new Date(form.scheduled_at).toISOString().slice(0, 19).replace('T', ' ') };
      if (form.type === 'single') { data.phone = form.phone; data.message = form.message; if (form.template_id) data.template_id = form.template_id; }
      else { if (form.recipient_tag) data.recipient_tag = form.recipient_tag; else data.recipients = form.recipients.split(/[,\n]+/).map((n: string) => n.trim()).filter(Boolean); data.template_id = form.template_id; data.template_values = form.template_values; }
      await createWaScheduled(data);
      toast.show('Scheduled!', 'success');
      setCreating(false);
      setForm({ type: 'single', phone: '', message: '', template_id: '', template_values: {}, recipients: '', recipient_tag: '', scheduled_at: '' });
      load();
    } catch (e: any) { toast.show(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const handleCancel = async (id: number) => {
    if (!confirm('Cancel this scheduled message?')) return;
    try { await cancelWaScheduled(id); toast.show('Cancelled', 'success'); load(); } catch (e: any) { toast.show(e.message, 'error'); }
  };

  const handleProcess = async () => {
    try { const r: any = await processWaScheduled(); toast.show(`Processed ${r.processed}: ${r.sent} sent, ${r.failed} failed`, 'success'); load(); }
    catch (e: any) { toast.show(e.message, 'error'); }
  };

  const statusColors: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-700',
    processing: 'bg-blue-100 text-blue-700',
    sent: 'bg-green-100 text-green-700',
    partial: 'bg-amber-100 text-amber-700',
    failed: 'bg-red-100 text-red-700',
    cancelled: 'bg-slate-100 text-slate-500',
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-700">Scheduled Messages ({scheduled.length})</h2>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={handleProcess} leftIcon={<PlayCircle size={12} />}>Process Due</Button>
          <Button size="sm" onClick={() => setCreating(!creating)} leftIcon={<Plus size={14} />}>Schedule Message</Button>
        </div>
      </div>

      {creating && (
        <Card className="p-5 space-y-3 border-2 border-green-300">
          <h3 className="text-sm font-bold">Schedule New Message</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-600">Type</label>
              <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} className="w-full text-xs border rounded px-2 py-1.5">
                <option value="single">Single Message</option><option value="broadcast">Broadcast</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-600">Schedule At *</label>
              <input type="datetime-local" value={form.scheduled_at} onChange={e => setForm({ ...form, scheduled_at: e.target.value })} className="w-full text-xs border rounded px-2 py-1.5" />
            </div>
          </div>
          {form.type === 'single' ? (
            <>
              <Input label="Phone *" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="9876543210" />
              <Input label="Message (or select template below)" multiline value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} rows={3} />
            </>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <Input label="Phone Numbers (comma/newline)" multiline value={form.recipients} onChange={e => setForm({ ...form, recipients: e.target.value })} rows={3} />
              <Input label="Or Use Contact Tag" value={form.recipient_tag} onChange={e => setForm({ ...form, recipient_tag: e.target.value })} placeholder="vip" />
            </div>
          )}
          <div>
            <label className="text-xs text-slate-600">Template (optional for single, required for broadcast)</label>
            <select value={form.template_id} onChange={e => setForm({ ...form, template_id: e.target.value })} className="w-full text-xs border rounded px-2 py-1.5">
              <option value="">None (send as text)</option>
              {templates.map((t: any) => <option key={t.id || t.wid} value={t.id || t.wid}>{t.name}</option>)}
            </select>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={handleCreate} isLoading={saving} disabled={!form.scheduled_at} leftIcon={<Calendar size={12} />}>Schedule</Button>
            <Button size="sm" variant="ghost" onClick={() => setCreating(false)}>Cancel</Button>
          </div>
        </Card>
      )}

      {loading ? <div className="flex justify-center py-8"><Loader2 className="animate-spin text-slate-400" size={24} /></div> :
      <div className="space-y-2">
        {scheduled.map((s: any) => (
          <Card key={s.id} className="p-4">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <Clock size={14} className="text-slate-400" />
                <span className="text-sm font-medium">{s.type === 'single' ? `To: ${s.phone}` : `Broadcast${s.recipient_tag ? ` (tag: ${s.recipient_tag})` : ` (${(s.recipients || []).length} numbers)`}`}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${statusColors[s.status] || ''}`}>{s.status}</span>
              </div>
              <div className="flex items-center gap-1">
                {s.status === 'pending' && <button onClick={() => handleCancel(s.id)} className="px-2 py-1 text-xs rounded border text-red-500 hover:bg-red-50">Cancel</button>}
              </div>
            </div>
            <div className="text-xs text-slate-500 space-y-0.5">
              <div><b>Scheduled:</b> {new Date(s.scheduled_at).toLocaleString()}</div>
              {s.message && <div className="line-clamp-1"><b>Message:</b> {s.message}</div>}
              {s.template_name && <div><b>Template:</b> {s.template_name}</div>}
              {s.sent_count > 0 && <div><b>Result:</b> {s.sent_count} sent, {s.failed_count} failed</div>}
            </div>
          </Card>
        ))}
        {scheduled.length === 0 && (
          <Card className="p-8 text-center">
            <Clock size={40} className="mx-auto mb-3 text-slate-300" />
            <p className="text-sm text-slate-500">No scheduled messages. Create one to send messages at a specific time.</p>
          </Card>
        )}
      </div>}
    </div>
  );
};
